import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { fuseAnimations } from '@fuse/animations';
import {
  TaskStatusService,
  TaskTypeService,
} from '../../../../shared/services/task-type.service';
import { TaskService } from '../../../../shared/services/task.service';
import { TaskStatusType, TaskType } from 'app/shared/models/task-type';
import { Task } from 'app/shared/models/task';
import { User } from 'app/shared/models/user';
import { UserService } from 'app/shared/services/user.service';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { TaskDialogComponent } from '../task-dialog/task-dialog.component';
import { map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Utilities } from 'app/shared/helpers/utilities';
import { environment } from 'environments/environment';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from 'app/components/messageBox/messageBox.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

@Component({
  selector: 'fuse-task-details',
  templateUrl: './task-details.component.html',
  styleUrls: ['./task-details.component.scss'],
  animations: fuseAnimations,
})
export class FuseTaskDetailsComponent
  implements OnInit, OnDestroy, AfterViewInit {
  formType: string;
  taskForm: UntypedFormGroup;
  labels: string[] = [];
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  @ViewChild('titleInput') titleInputField;

  @Input() allowEdit = false;
  @Input() inWidget = false;
  @Input() showLink = false;
  @Input() task: Task = null;
  @Input() dialogRef: MatDialogRef<TaskDialogComponent> = null;
  @Input() bindTaskToList = false;

  onFormChange: Subscription;
  onSelected: Subscription;
  onDateDueValueChanges: Subscription;

  formControl = new UntypedFormControl('', [Validators.required]);

  public taskTypes: TaskType[] = [];
  public taskStatus: TaskStatusType[] = [];
  public userList: User[] = [];
  public readOnly = true;
  public maxDate: Date;
  public minDate: Date;

  public TaskStarred = false;
  public TaskImportant = false;
  public unallowedValues = ["Completed", "Abandoned"];

  public validation_messages = {
    summary: [
      { type: 'required', message: 'Required' },
      {
        type: 'maxlength',
        message: 'Summary cannot be more than 100 characters long',
      },
    ],
    labels: [
      {
        type: 'maxLength',
        message: 'Labels cannot be more than 500 characters long',
      },
    ],
  };

  constructor(
    private formBuilder: UntypedFormBuilder,
    private taskStatusService: TaskStatusService,
    public subjectTypeService: SubjectTypeService,
    private userService: UserService,
    private taskTypeService: TaskTypeService,
    public dialog: MatDialog,
    private taskService: TaskService,
  ) {
    this.getTaskTypes();
    this.taskStatus = this.taskStatusService.getTaskStatusTypes();
    this.minDate = dayjs.utc().startOf('day').toDate();
    this.maxDate = dayjs
      .utc()
      .endOf('day')
      .add(10, 'years')
      .endOf('year')
      .toDate();
  }

  private getActiveUsers(currentUserId?: number) {
    this.userService.clearCache();
    if (
      environment.limitAccessToSubjectsByOrg === true &&
      this.task &&
      this.task.subject &&
      this.task.organisationId > 0
    ) {
      this.userService
        .getActiveUsersForOrganisation(this.task.organisationId, false)
        .pipe(take(1))
        .subscribe((users) => {
          this.userList = [...users];
        });
    } else {
      this.userService
        .getActiveUsers(currentUserId)
        .pipe(take(1))
        .subscribe((users) => {
          this.userList = [...users];
        });
    }
  }

  getTaskTypes(): TaskType[] {
    this.taskTypes = this.taskTypeService.getActiveTaskTypesList();
    if (this.task && (!this.task.subjectId || this.task.subjectId < 1)) {
      this.taskTypes =
        this.taskTypeService.getActiveTaskTypesWithoutAttachmentsList();
    }
    return this.taskTypes;
  }

  ngOnInit() {
    this.onSelected = this.taskService.onSelected.subscribe((task: Task) => {
      this.setTask(task);
    });

    this.formType = 'new';
    this.setTask(this.task);
  }

  ngAfterViewInit(): void {
    Utilities.applyChipListAccessibilityHacks();
  }

  ngOnDestroy() {
    if (this.bindTaskToList === true) {
      if (this.onFormChange) {
        this.onFormChange.unsubscribe();
      }
    }
    if (this.onSelected) {
      this.onSelected.unsubscribe();
    }
    this.onDateDueValueChanges?.unsubscribe();
  }

  setTask(task: Task) {
    this.task = task;
    this.getTaskTypes();
    if (task && task.actionForId > 0) {
      this.getActiveUsers(this.task.actionForId);
    } else {
      this.getActiveUsers(this.userService.getCurrentUserId());
    }
    if (task) {
      this.TaskImportant = false || this.task.important;
      this.TaskStarred = false || this.task.starred;
    }
    this.allowEdit =
      this.allowEdit &&
      !(this.task && this.task.readOnly) &&
      !(this.task && this.task.subject && this.task.subject.archived);
    this.taskForm = this.createTaskForm();
  }

  focusTitleField() {
    setTimeout(() => {
      this.titleInputField.nativeElement.focus();
    });
  }

  createTaskForm() {
    if (!this.task) {
      return null;
    }

    // setup labels
    this.labels = this.task.labels ? this.task.labels.split(',') : [];

    // Ensure Task passed in is hydrated with objects as they are required for display
    if (!this.task.taskType && this.task.taskTypeId > 0) {
      this.task.taskType = this.taskTypeService.getTaskTypeById(
        this.task.taskTypeId
      );
    }
    if (!this.task.actionedBy && this.task.actionedById > 0) {
      this.task.actionedBy = this.userService.getUserById(
        this.task.actionedById
      );
    }
    if (!this.task.actionFor && this.task.actionForId > 0) {
      this.task.actionFor = this.userService.getUserById(this.task.actionForId);
    }
    if (!this.task.createdBy && this.task.createdById > 0) {
      this.task.createdBy = this.userService.getUserById(this.task.createdById);
    }

    const form = this.formBuilder.group({
      id: [this.task.id],
      uid: [this.task.uid],
      summary: [
        this.task.summary,
        Validators.compose([Validators.maxLength(100)]),
      ],
      dateCreated: [this.task.dateCreated],
      dateDue: [this.task.dateDue, Validators.required],
      dateCompleted: [this.task.dateCompleted],
      starred: [this.task.starred || false],
      important: [this.task.important || false],
      labels: [this.task.labels || '', Validators.maxLength(500)],
      taskTypeId: [this.task.taskTypeId || null, [Validators.required]],
      taskTypeName: [this.task.taskType ? this.task.taskType.name : null],
      taskStatus: [this.task.taskStatus],
      taskStatusName: [this.task.taskStatusName],
      actionForId: [this.task.actionForId, [Validators.required]],
      actionedById: [this.task.actionedById],
      createdById: [this.task.createdById],
      subjectId: [this.task.subjectId],
      state: [this.task.state],
      notes: [this.task.notes],
      dateCompletedNo: [],
    });

    if (this.task.taskType && this.task.taskType.hideRecord) {
      form.controls['taskTypeId'].disable();
      this.taskTypes.push(this.task.taskType);
    }

    if (this.task.actionFor && !this.task.actionFor.canLogin) {
      form.controls['actionForId'].disable();
      this.userList.push(this.task.actionFor);
    }

    if (this.task.createdBy && !this.task.createdBy.canLogin) {
      this.userList.push(this.task.createdBy);
    }

    if (this.task.actionedBy && !this.task.actionedBy.canLogin) {
      form.controls['actionedById'].disable();
      this.userList.push(this.task.actionedBy);
    }

    form.controls['createdById'].disable();
    form.controls['actionedById'].disable();
    form.controls['dateCompleted'].disable();
    form.controls['dateCreated'].disable();
    form.controls['taskStatus'].disable();
    form.controls['taskStatusName'].disable();
    form.controls['dateCompletedNo'].disable();


    this.onDateDueValueChanges = form.controls['dateDue'].valueChanges.subscribe((dateDue) => {
      // Update task state when Due date is changed
      var today = dayjs.utc().startOf('day').toDate();

      if (!this.unallowedValues.includes(this.task.taskStatusName)) {

        if (dateDue < today) {
          form.controls['taskStatus'].setValue(20);
          form.controls['taskStatusName'].setValue('Late');
        } else {
          form.controls['taskStatus'].setValue(10);
          form.controls['taskStatusName'].setValue('Open');
        }

      }


    });

    if (this.allowEdit === false || this.task.taskStatus >= 30) {
      // Do not allow editting when allowEdit is false (read only view) or if task is completed or abandoned.
      form.disable();
    }
    this.taskForm = form;
    return form;
  }

  toggleStar(event) {
    event.stopPropagation();
    this.task.starred = !this.task.starred;
    this.taskForm.controls['starred'].setValue(this.task.starred);
    this.taskForm.controls['starred'].markAsDirty();
  }

  toggleImportant(event) {
    event.stopPropagation();
    this.task.important = !this.task.important;
    this.taskForm.controls['important'].setValue(this.task.important);
    this.taskForm.controls['important'].markAsDirty();
  }

  saveTask() {
    // Save task
    const editedTask: Task = new Task(this.taskForm.getRawValue());
    editedTask.organisationId = this.task.organisationId;
    editedTask.subject = this.task.subject;
    editedTask.labels = this.labels.join(', ');
    //editedTask.notes = this.task.notes;

    const taskType =
      editedTask.taskType ||
      this.taskTypeService.getByIdFromCache(editedTask.taskTypeId);
    let save$ = of(true);
    if (taskType && taskType.keyEvent === true) {

      var c = editedTask?.subject?.referral;
      var info: MessageBoxParameters;

      if (c?.referralSource?.name == "File a Concern Website") {
        if (environment.limitAccessToSubjectsByOrg === true) {
          info = new MessageBoxParameters(
            'The whistle-blower will be notified of this event once you have marked this Task as completed on the system. Do you want to continue?',
            'Key Event',
            MessageBoxType.YesNo
          );
        }
        else {
          info = new MessageBoxParameters(
            'If the whistle-blower has provided an email address, then they will be notified of this event once it has been marked as completed. Do you want to continue?',
            'Key Event',
            MessageBoxType.YesNo
          );
        }
      }
      if (info) {
        save$ = this.dialog
          .open(MessageBoxComponent, { data: info })
          .afterClosed()
          .pipe(
            map((result) => {
              return result === 'yes';
            })
          );
      }
    }

    save$.pipe(take(1)).subscribe((result) => {
      if (result) {
        this.taskService
          .save(editedTask, `Task "${editedTask.summary}" saved`)
          .subscribe((closeDlg) => {
            // Close dialog box.
            if (this.dialogRef && closeDlg === true) {
              this.dialogRef.close(editedTask);
            }
          });
      } else {
        // chose not to continue - reset task type on form
        this.taskForm.controls['taskTypeId'].setValue(undefined);
      }
    });
  }

  getErrorMessage() {
    return this.taskForm.hasError('required') ? 'Required field' : '';
  }

  public canClose(): Observable<boolean> {
    // MRD - not sure why we need this and is causing problems for new tasks as will always be dirty even if user has made no changes
    // Commented out and see what feedback, if any, we get....
    // if (this.taskForm.dirty) {
    //     const info = new MessageBoxParameters('You have unsaved changes.  Discard?', 'Unsaved changes', MessageBoxType.YesNo);
    //     return this.dialog.open(MessageBoxComponent, { data: info }).afterClosed().pipe(map((result) => {
    //         const yesNo = (result === 'yes');

    //         if (yesNo === true)
    //         {
    //             this.task.important = this.TaskImportant;
    //             this.task.starred = this.TaskStarred;
    //         }

    //         return yesNo;
    //     }));
    // }
    return of(true);
  }

  addLabel(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    // Add label
    if ((value || '').trim()) {
      this.labels.push(value.trim());
    }

    // Reset the input value
    if (input) {
      input.value = '';
    }

    // mark the form as dirty - this ensures save button is enabled
    this.taskForm.markAsDirty();
  }

  removeLabel(label: string): void {
    const index = this.labels.indexOf(label);

    if (index >= 0) {
      this.labels.splice(index, 1);
    }

    // mark the form as dirty - this ensures save button is enabled
    this.taskForm.markAsDirty();
  }
}
